// LegalPage.jsx - Full Page Legal Component
import React, { useState, useEffect } from 'react';
import { Shield, FileText, Eye, Lock, Zap, AlertTriangle, CheckCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LegalPage = ({ type = 'privacy' }) => {
  const [activeTab, setActiveTab] = useState(type);
  const navigate = useNavigate();
  const location = useLocation();

  // Set active tab based on current URL
  useEffect(() => {
    if (location.pathname === '/privacy') {
      setActiveTab('privacy');
    } else if (location.pathname === '/terms') {
      setActiveTab('terms');
    }
  }, [location.pathname]);

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
          
          {/* Header */}
          <div className="bg-slate-800/50 border-b border-slate-700 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Legal Information</h2>
                  <p className="text-slate-400">Privacy Policy & Terms of Service</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all text-slate-300 hover:text-white"
              >
                ← Back to Home
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setActiveTab('privacy');
                  navigate('/privacy');
                }}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  activeTab === 'privacy'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <Lock className="w-4 h-4" />
                Privacy Policy
              </button>
              <button
                onClick={() => {
                  setActiveTab('terms');
                  navigate('/terms');
                }}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  activeTab === 'terms'
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <FileText className="w-4 h-4" />
                Terms of Service
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {activeTab === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-800/30 border-t border-slate-700 px-8 py-4">
            <div className="flex items-center justify-between text-sm">
              <div className="text-slate-400">
                Last updated: {currentDate}
              </div>
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>GDPR & CCPA Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivacyPolicy = () => (
  <div className="text-slate-300 space-y-8">
    {/* Header */}
    <div className="text-center mb-8">
      <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <p className="text-blue-300 text-lg leading-relaxed">
          <Shield className="w-5 h-5 inline mr-2" />
          At CodeRated, we take your privacy seriously. This policy explains how we collect, use, and protect your information when you use our AI-powered website analysis platform.
        </p>
      </div>
    </div>

    {/* Information We Collect */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <Eye className="w-6 h-6 text-blue-400" />
        Information We Collect
      </h2>
      
      <div className="space-y-4">
        <div className="bg-slate-700/30 rounded-lg p-4">
          <h3 className="font-semibold text-white mb-2">Account Information</h3>
          <p className="text-slate-300">Name, email address, password (encrypted), and subscription tier when you create an account.</p>
        </div>
        
        <div className="bg-slate-700/30 rounded-lg p-4">
          <h3 className="font-semibold text-white mb-2">Website Analysis Data</h3>
          <p className="text-slate-300">URLs you submit for analysis, analysis results, and associated metadata. This helps us provide and improve our AI analysis service.</p>
        </div>
        
        <div className="bg-slate-700/30 rounded-lg p-4">
          <h3 className="font-semibold text-white mb-2">Usage Information</h3>
          <p className="text-slate-300">How you interact with our platform, including features used, analysis frequency, and performance metrics.</p>
        </div>
        
        <div className="bg-slate-700/30 rounded-lg p-4">
          <h3 className="font-semibold text-white mb-2">Technical Data</h3>
          <p className="text-slate-300">IP address, browser type, device information, and cookies for functionality and analytics.</p>
        </div>
      </div>
    </section>

    {/* How We Use Information */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <Zap className="w-6 h-6 text-purple-400" />
        How We Use Your Information
      </h2>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-green-400 mb-2">Service Delivery</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Perform AI website analysis</li>
            <li>• Provide analysis results and insights</li>
            <li>• Maintain your account and preferences</li>
            <li>• Process payments and subscriptions</li>
          </ul>
        </div>
        
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-blue-400 mb-2">Platform Improvement</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Train and improve our AI models</li>
            <li>• Develop new features and capabilities</li>
            <li>• Monitor platform performance</li>
            <li>• Provide customer support</li>
          </ul>
        </div>
        
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-purple-400 mb-2">Communication</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Send important account updates</li>
            <li>• Notify about new features</li>
            <li>• Provide customer support</li>
            <li>• Marketing (with your consent)</li>
          </ul>
        </div>
        
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-orange-400 mb-2">Legal Compliance</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Comply with applicable laws</li>
            <li>• Protect against fraud and abuse</li>
            <li>• Enforce our terms of service</li>
            <li>• Respond to legal requests</li>
          </ul>
        </div>
      </div>
    </section>

    {/* Data Sharing */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Information Sharing</h2>
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
        <p className="text-red-300 font-semibold flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          We do NOT sell your personal information to third parties.
        </p>
      </div>
      
      <p className="mb-4">We may share information only in these limited circumstances:</p>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
          <span><strong>Service Providers:</strong> Trusted partners who help operate our platform (hosting, payments, analytics) under strict data protection agreements.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
          <span><strong>Legal Requirements:</strong> When required by law, legal process, or to protect rights and safety.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
          <span><strong>Business Transfer:</strong> In case of merger, acquisition, or sale of assets (with advance notice).</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
          <span><strong>Public Feed:</strong> Analysis results you choose to share publicly (website URLs and scores only, never personal data).</span>
        </li>
      </ul>
    </section>

    {/* Your Rights */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Your Privacy Rights</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <h3 className="font-semibold text-blue-400 mb-1">Access & Download</h3>
            <p className="text-sm">Request a copy of your personal data in portable format.</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <h3 className="font-semibold text-green-400 mb-1">Correction</h3>
            <p className="text-sm">Update or correct inaccurate personal information.</p>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
            <h3 className="font-semibold text-purple-400 mb-1">Deletion</h3>
            <p className="text-sm">Request deletion of your account and personal data.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
            <h3 className="font-semibold text-orange-400 mb-1">Opt-Out</h3>
            <p className="text-sm">Unsubscribe from marketing communications anytime.</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <h3 className="font-semibold text-red-400 mb-1">Restrict Processing</h3>
            <p className="text-sm">Limit how we process your personal information.</p>
          </div>
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
            <h3 className="font-semibold text-cyan-400 mb-1">Portability</h3>
            <p className="text-sm">Transfer your data to another service provider.</p>
          </div>
        </div>
      </div>
      <div className="mt-4 p-4 bg-slate-700/30 rounded-lg">
        <p className="text-center text-slate-300">
          To exercise your rights, contact us at <strong className="text-white">privacy@coderated.com</strong>
        </p>
      </div>
    </section>

    {/* Data Security & Retention */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Data Security & Retention</h2>
      <div className="space-y-4">
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-green-400 mb-2">Security Measures</h3>
          <p className="text-slate-300">We use industry-standard encryption, secure servers, regular security audits, and access controls to protect your data.</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-blue-400 mb-2">Data Retention</h3>
          <p className="text-slate-300">We retain your data only as long as necessary for service delivery, legal compliance, and legitimate business purposes. Account data is deleted within 30 days of account closure.</p>
        </div>
      </div>
    </section>

    {/* Contact */}
    <section className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6 text-center">
      <h2 className="text-2xl font-bold text-white mb-2">Questions About Privacy?</h2>
      <p className="text-slate-300 mb-4">We're here to help with any privacy-related questions or concerns.</p>
      <div className="text-blue-400 font-semibold">
        Email: privacy@coderated.com<br/>
        Response time: Within 48 hours
      </div>
    </section>
  </div>
);

const TermsOfService = () => (
  <div className="text-slate-300 space-y-8">
    {/* Header */}
    <div className="text-center mb-8">
      <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
        <p className="text-purple-300 text-lg leading-relaxed">
          <FileText className="w-5 h-5 inline mr-2" />
          These terms govern your use of CodeRated's AI website analysis platform. By using our service, you agree to these terms.
        </p>
      </div>
    </div>

    {/* Service Description */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Our Service</h2>
      <p className="mb-4">CodeRated provides AI-powered website analysis including:</p>
      <div className="grid md:grid-cols-2 gap-4">
        <ul className="space-y-2">
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Design & UX Analysis</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Performance Optimization</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />SEO Assessment</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Accessibility Review</li>
        </ul>
        <ul className="space-y-2">
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Content Analysis</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Competitive Intelligence</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Community Feed</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Business Insights</li>
        </ul>
      </div>
    </section>

    {/* User Responsibilities */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Your Responsibilities</h2>
      <div className="space-y-4">
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-green-400 mb-2">✅ Acceptable Use</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Use the service for legitimate website analysis</li>
            <li>• Provide accurate account information</li>
            <li>• Respect other users and our community guidelines</li>
            <li>• Keep your account credentials secure</li>
          </ul>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-red-400 mb-2">❌ Prohibited Activities</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Analyzing illegal, harmful, or malicious websites</li>
            <li>• Attempting to circumvent usage limits or security measures</li>
            <li>• Reverse engineering or copying our AI models</li>
            <li>• Reselling or redistributing our analysis results</li>
            <li>• Submitting false, misleading, or spam content</li>
          </ul>
        </div>
      </div>
    </section>

    {/* AI Analysis Disclaimer */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <AlertTriangle className="w-6 h-6 text-yellow-400" />
        AI Analysis Disclaimer
      </h2>
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
        <p className="text-yellow-200 mb-3">
          <strong>Important:</strong> Our AI analysis provides automated insights and recommendations based on technical analysis of websites.
        </p>
        <ul className="text-slate-300 space-y-2 text-sm">
          <li>• <strong>Not Professional Advice:</strong> Results are informational and should not replace professional web development, marketing, or business consulting.</li>
          <li>• <strong>Accuracy:</strong> While we strive for accuracy, AI analysis may contain errors or miss important context.</li>
          <li>• <strong>No Guarantees:</strong> Analysis results don't guarantee website performance improvements or business outcomes.</li>
          <li>• <strong>Your Judgment:</strong> Always use your professional judgment when implementing recommendations.</li>
        </ul>
      </div>
    </section>

    {/* Subscription & Billing */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Subscription & Billing</h2>
      <div className="space-y-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-blue-400 mb-2">Billing Terms</h3>
          <p className="text-slate-300 text-sm">Subscriptions are billed monthly/annually in advance. Usage limits reset each billing cycle. Prices may change with 30 days notice.</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-purple-400 mb-2">Cancellation</h3>
          <p className="text-slate-300 text-sm">Cancel anytime. Service continues until the end of your billing period. No refunds for partial billing periods except as required by law.</p>
        </div>
      </div>
    </section>

    {/* Intellectual Property */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-green-400 mb-2">Your Content</h3>
          <p className="text-slate-300 text-sm">You retain ownership of websites you submit for analysis. By using our service, you grant us rights to analyze and process your submitted URLs.</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-blue-400 mb-2">Our Platform</h3>
          <p className="text-slate-300 text-sm">CodeRated owns all rights to our platform, AI models, analysis algorithms, and generated insights. These are protected by copyright and trade secret laws.</p>
        </div>
      </div>
    </section>

    {/* Liability & Warranties */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
        <p className="text-orange-200 text-sm mb-3">
          <strong>Service "As Is":</strong> CodeRated is provided "as is" without warranties. We don't guarantee uninterrupted access, analysis accuracy, or specific outcomes.
        </p>
        <p className="text-slate-300 text-sm">
          <strong>Liability Limit:</strong> Our liability is limited to the amount you've paid in the 12 months before any claim. We're not liable for indirect, incidental, or consequential damages.
        </p>
      </div>
    </section>

    {/* Termination */}
    <section className="bg-slate-800/50 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Account Termination</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-red-400 mb-2">We May Terminate</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Violation of these terms</li>
            <li>• Abuse of our service</li>
            <li>• Non-payment of fees</li>
            <li>• Legal compliance requirements</li>
          </ul>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-blue-400 mb-2">You May Terminate</h3>
          <ul className="text-slate-300 space-y-1 text-sm">
            <li>• Cancel subscription anytime</li>
            <li>• Delete your account in settings</li>
            <li>• Request data deletion</li>
            <li>• Stop using our service</li>
          </ul>
        </div>
      </div>
    </section>

    {/* Changes & Contact */}
    <section className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Changes & Contact</h2>
      <div className="space-y-4">
        <p className="text-slate-300">
          <strong>Terms Updates:</strong> We may update these terms occasionally. Major changes will be notified via email or platform notice 30 days in advance.
        </p>
        <p className="text-slate-300">
          <strong>Governing Law:</strong> These terms are governed by the laws of [Your Jurisdiction] without regard to conflict of law principles.
        </p>
        <div className="text-center mt-6 p-4 bg-slate-700/30 rounded-lg">
          <p className="text-slate-300 mb-2">Questions about these terms?</p>
          <div className="text-purple-400 font-semibold">
            Email: legal@coderated.com<br/>
            Response time: Within 48 hours
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default LegalPage;